/**
 * Generic hook for patch-based document saves
 * 
 * Implements:
 * - Direct patch → API persistence
 * - Server echo validation (prevents silent snap-back)
 * - Optimistic cache updates with rollback on error
 * - Proper cache invalidation order
 * 
 * @example
 * ```tsx
 * const saveMutation = usePatchSaveDoc({
 *   doctype: "Item",
 *   docname: "ITEM-001",
 *   detailKey: ["item", "ITEM-001"],
 * });
 * 
 * await saveMutation.mutateAsync({ field1: "value1" });
 * ```
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { trackEvent } from "@/lib/activity-tracker/tracker";
import { captureDocSaved } from "@/lib/analytics/posthog-events";

type Patch = Record<string, any>;

export interface UsePatchSaveDocOptions {
  /**
   * DocType name (e.g., "Item", "Sales Order")
   */
  doctype: string;
  /**
   * Document name/identifier
   */
  docname: string;
  /**
   * React Query key for the detail query (e.g., ["item", "ITEM-001"])
   * Used for cache updates and invalidation
   */
  detailKey: readonly unknown[];
}

/**
 * Hook for patch-based document saves
 * 
 * R1: Patch save must be "real save" - calls API directly
 * R2: No silent success - throws if patch is non-empty but save fails
 * R3: Server echo validation - verifies server persisted the patch
 * R4: Cache update on success - updates cache before invalidation
 */
export function usePatchSaveDoc(opts: UsePatchSaveDocOptions) {
  const { doctype, docname, detailKey } = opts;
  const queryClient = useQueryClient();

  return useMutation<
    any, // Updated document (returned from API)
    Error, // Error type
    Patch, // Mutation input (the patch)
    { previous: any } // Context for rollback
  >({
    mutationFn: async (patch: Patch) => {
      // R1: If patch is empty, return early (no-op)
      if (!patch || Object.keys(patch).length === 0) {
        return null;
      }

      // R1: Real persistence - call API directly
      const updated = await getERPClient().update({
        doctype,
        name: docname,
        data: patch,
      });

      // R3: Server echo validation - verify server actually persisted the patch
      //
      // For child tables (arrays): we only verify the array is present and has the
      // expected length. Per-field comparison is intentionally NOT done because
      // ERPNext server-side document controllers recompute derived fields on save
      // (e.g., Stock Entry Detail recomputes `transfer_qty`, `basic_amount`,
      // `amount`, `valuation_rate` from `qty * conversion_factor`, `qty * basic_rate`).
      // The server is authoritative for child table state. If we strict-compared
      // every field, every ERPNext-computed field would look like a "server didn't
      // persist my data" failure and roll back a save that actually succeeded.
      //
      // For scalar fields: keep strict comparison — catches silent server drops.
      const validationErrors: string[] = [];
      for (const [key, expectedValue] of Object.entries(patch)) {
        const actualValue = (updated as any)?.[key];

        if (Array.isArray(expectedValue)) {
          if (!Array.isArray(actualValue)) {
            validationErrors.push(
              `Field "${key}": expected array but got ${typeof actualValue}`
            );
            continue;
          }

          if (expectedValue.length !== actualValue.length) {
            validationErrors.push(
              `Field "${key}": expected array length ${expectedValue.length} but got ${actualValue.length}`
            );
            continue;
          }

          // Trust the server for child row contents. Derived fields belong to
          // the server (ERPNext protocol). The onSuccess handler below writes
          // the full server response to the cache, so the UI will show the
          // authoritative values on next render.
          continue;
        }

        // For scalar fields, strict comparison catches silent server-side drops.
        const valuesMatch = JSON.stringify(actualValue) === JSON.stringify(expectedValue);

        if (!valuesMatch) {
          validationErrors.push(
            `Field "${key}": expected ${JSON.stringify(expectedValue)} but got ${JSON.stringify(actualValue)}`
          );
        }
      }

      if (validationErrors.length > 0) {
        const errorMessage = `Server did not persist changes:\n${validationErrors.join("\n")}`;
        throw new Error(errorMessage);
      }

      return updated;
    },
    
    // Optimistic update with rollback
    onMutate: async (patch: Patch) => {
      if (!patch || Object.keys(patch).length === 0) {
        return { previous: null };
      }

      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: detailKey });

      // Snapshot the previous value for rollback
      const previous  = queryClient.getQueryData(detailKey);

      // Optimistically update cache with merged data
      queryClient.setQueryData(detailKey, (old: any) => {
        if (!old) return { ...patch };
        return { ...old, ...patch };
      });

      return { previous: previous ?? null };
    },
    
    // Rollback on error
    onError: (_err, _patch, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(detailKey, context.previous);
      }
    },
    
    // R4: Cache update on success (before invalidation)
    onSuccess: (updatedDoc, variables) => {
      if (!updatedDoc) return;

      // Update cache with server-confirmed data
      queryClient.setQueryData(detailKey, updatedDoc);

      // Track doc_save event for activity stream
      trackEvent({
        event_type: 'doc_save',
        reference_doctype: doctype,
        doc_name: docname,
      });
      captureDocSaved(doctype, docname, Object.keys(variables));
    },
  });
}

